import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { LEAD_STATUSES, StatusBadge } from "@/pages/admin/AdminLeads";
import { toast } from "sonner";

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs tracking-[0.15em] uppercase text-slate-500">{label}</p>
    <p className="mt-1.5 text-slate-200 text-sm leading-relaxed">{value || "-"}</p>
  </div>
);

export default function AdminStudent() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");

  const load = () => {
    api.get(`/admin/students/${userId}`).then((r) => {
      setData(r.data);
      setNotes(r.data.student.notes || "");
    }).catch(() => toast.error("Could not load student"));
  };

  useEffect(() => { load(); }, [userId]);

  const update = async (patch) => {
    try {
      await api.patch(`/admin/students/${userId}`, patch);
      toast.success("Saved");
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  if (!data) return <AdminLayout title="Student Profile"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></AdminLayout>;

  const s = data.student;

  return (
    <AdminLayout title={s.first_name || s.name || "Student Profile"}>
      <button data-testid="back-to-leads" onClick={() => navigate("/admin/leads")}
        className="flex items-center gap-2 text-sm text-slate-400 mb-6 transition-colors duration-300 hover:text-[#D4AF37]">
        <ArrowLeft className="w-4 h-4" /> Back to leads
      </button>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-3xl border border-white/10 bg-white/[0.03] p-8" data-testid="student-profile-card">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
            <StatusBadge status={s.lead_status} />
            <select
              data-testid="lead-status-select"
              value={s.lead_status}
              onChange={(e) => update({ lead_status: e.target.value })}
              className="bg-white/5 border border-white/10 rounded-full px-5 py-2.5 text-sm text-white outline-none [&>option]:bg-[#0A1633]"
            >
              {LEAD_STATUSES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            <Field label="Name" value={s.first_name || s.name} />
            <Field label="WhatsApp" value={s.whatsapp ? `${s.whatsapp_country_code || ""} ${s.whatsapp}` : null} />
            <Field label="Email" value={s.email} />
            <Field label="University" value={s.university} />
            <Field label="Course" value={s.course} />
            <Field label="UK City" value={s.uk_city} />
            <Field label="Indian City" value={s.indian_city} />
            <Field label="Arrival Month" value={s.arrival_month} />
            <Field label="Flight Date" value={s.flight_date} />
            <Field label="Flight Booked" value={s.flight_booked} />
            <Field label="Accommodation" value={s.accommodation_booked} />
            <Field label="Community Interest" value={s.community_interest} />
            <Field label="Source" value={s.source} />
            <Field label="Signup Date" value={(s.created_at || "").slice(0, 10)} />
            <Field label="Last Login" value={s.last_login ? s.last_login.replace("T", " ").slice(0, 16) : null} />
            <Field label="Email Verified" value={s.email_verified ? "Yes" : "No"} />
            <Field label="Journey Progress" value={`${data.journey.completed}/${data.journey.total} tasks`} />
            <Field label="Packing Progress" value={data.packing ? `${data.packing.packed}/${data.packing.total} items` : "0 items"} />
            <Field label="AI Questions Asked" value={String(data.ai_usage_count)} />
          </div>
          <div className="mt-8">
            <p className="text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">Main Goals</p>
            <div className="flex flex-wrap gap-2">
              {(s.goals || []).map((g) => (
                <span key={g} className="text-xs rounded-full border border-[#D4AF37]/30 text-[#D4AF37] px-4 py-1.5">{g}</span>
              ))}
              {!s.goals?.length && <span className="text-sm text-slate-500">-</span>}
            </div>
          </div>
          <div className="mt-8">
            <p className="text-xs tracking-[0.15em] uppercase text-slate-500 mb-3">Notes</p>
            <textarea
              data-testid="admin-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add internal notes about this student..."
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-sm text-white placeholder-slate-500 outline-none transition-colors duration-300 focus:border-[#D4AF37]/70"
            />
            <button data-testid="save-notes-button" onClick={() => update({ notes })}
              className="mt-3 rounded-full bg-[#D4AF37] text-[#0A1633] font-semibold px-6 py-2.5 text-sm transition-all duration-300 hover:bg-[#e5c558]">
              Save notes
            </button>
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8" data-testid="activity-timeline">
          <h3 className="text-lg text-white font-display">Activity Timeline</h3>
          <div className="mt-6 space-y-5 max-h-[560px] overflow-y-auto pr-2">
            {data.activity.map((a) => (
              <div key={a.event_id} className="flex gap-4">
                <div className="w-2 h-2 rounded-full bg-[#D4AF37] mt-2 shrink-0" />
                <div>
                  <p className="text-sm text-slate-200">{a.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{(a.ts || "").replace("T", " ").slice(0, 16)}{a.page ? ` · ${a.page}` : ""}</p>
                </div>
              </div>
            ))}
            {!data.activity.length && <p className="text-sm text-slate-500">No activity yet</p>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
